import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isViDomain, VI_CONFIG } from '../constants/viIntegration';
import { viPostMessage } from '../utils/nativePermissions';
import { validateViToken, clearViAuth } from '../utils/viAuth';
import { auth, signInWithCustomToken } from '../config/firebase.config';

const ViAuthContext = createContext(null);

export const useViAuth = () => useContext(ViAuthContext);

export const ViAuthProvider = ({ children }) => {
  const [viAuthState, setViAuthState] = useState({
    isViApp: false,
    isValidating: true,
    isAuthenticated: false,
    error: null,
  });

  const location = useLocation();
  const navigate = useNavigate();
  // Capture initial search params before any navigation strips them
  const initialSearchRef = useRef(location.search);

  useEffect(() => {
    if (!isViDomain()) {
      setViAuthState(prev => ({ ...prev, isViApp: false, isValidating: false }));
      return;
    }

    const existingViUser = localStorage.getItem('isViUser') === 'true';
    const existingToken = localStorage.getItem('token');

    if (existingViUser && existingToken) {
      setViAuthState({
        isViApp: true,
        isValidating: false,
        isAuthenticated: true,
        error: null,
      });
      // Navigate away from root so ViRootRedirect (which returns null for Vi
      // auth params) doesn't leave a blank screen on re-open.
      const cleanPath = location.pathname === '/' || !location.pathname
        ? '/workspace'
        : location.pathname;
      navigate(cleanPath, { replace: true });
      return;
    }

    handleViAuth();
  }, []);

  const handleViAuth = async () => {
    console.log('[Vi Auth] handleViAuth called');
    try {
      const encryptedTimestamp = localStorage.getItem(VI_CONFIG.LOCALSTORAGE_HANDSHAKE_KEY);
      console.log('[Vi Auth] encryptedTimestamp:', encryptedTimestamp);
      if (!encryptedTimestamp && !window.location.hostname.includes('localhost')) {
        throw new Error('HANDSHAKE_MISSING');
      }

      // Use the captured initial search params to avoid losing them after navigation
      const params = new URLSearchParams(initialSearchRef.current);
      const data = params.get('data');
      const checksum = params.get('checksum');

      if (!data || !checksum) {
        throw new Error('MISSING_PARAMS');
      }

      const response = await validateViToken(data, checksum, encryptedTimestamp);

      if (!response.success) {
        throw new Error(response.error || 'VALIDATION_FAILED');
      }

      const userCredential = await signInWithCustomToken(auth, response.token);
      const idToken = await userCredential.user.getIdToken();

      localStorage.setItem('token', idToken);
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userEmail', response.user.email);
      localStorage.setItem('userId', response.user.userId);
      localStorage.setItem('isViUser', 'true');

      // Notify WorkspaceContext that auth completed so it can fetch onboarding status.
      // WorkspaceProvider mounts before ViAuthProvider, so its initial useEffect
      // runs when isLoggedIn is not yet set and skips the fetch.
      window.dispatchEvent(new Event('vi-auth-complete'));

      // Set authenticated state BEFORE navigating to prevent black screen.
      // If state is set after navigate(), the re-render cascade from navigation
      // can cause isValidating to remain true, showing a permanent loading screen.
      setViAuthState({
        isViApp: true,
        isValidating: false,
        isAuthenticated: true,
        error: null,
      });

      let cleanPath = location.pathname;
      if (!cleanPath || cleanPath === '/') {
        cleanPath = '/workspace';
      }
      navigate(cleanPath, { replace: true });

    } catch (error) {
      console.error('Vi auth error:', error);
      setViAuthState({
        isViApp: true,
        isValidating: false,
        isAuthenticated: false,
        error: error.message,
      });
    }
  };

  const goBackToViApp = () => {
    viPostMessage('navigation', { navigationType: 'go_back_vi_app' });
  };

  const viLogout = () => {
    clearViAuth();
    setViAuthState({
      isViApp: true,
      isValidating: false,
      isAuthenticated: false,
      error: 'LOGGED_OUT',
    });
    goBackToViApp();
  };

  return (
    <ViAuthContext.Provider value={{ ...viAuthState, goBackToViApp, viLogout }}>
      {children}
    </ViAuthContext.Provider>
  );
};

export default ViAuthContext;
